import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Spin, QRCode, Image, Modal, Descriptions, Alert, useApp } from '../../ui';
import { FileText, Store, MapPin, Landmark, Percent } from 'lucide-react';
import { request, api } from '../../api/client';
import { usePermission } from '../../store/auth';
import { formatIstDate } from '../../lib/time';
import { fmtQty } from '../../lib/format';
import { boxLoosePhrase } from '../../lib/packaging';
import { CreditNoteDrawer } from '../../components/CreditNoteDrawer';

const INV_PILL: Record<string, string> = {
  OPEN: 'bg-sky-500/10 text-sky-600',
  PARTIAL: 'bg-warning-500/10 text-warning-600',
  PAID: 'bg-success-500/10 text-success-600',
  OVERDUE: 'bg-danger-500/10 text-danger-600',
  CANCELLED: 'bg-surface-3 text-subtle',
};
const PAY_PILL: Record<string, string> = {
  APPROVED: 'bg-success-500/10 text-success-600',
  PENDING: 'bg-warning-500/10 text-warning-600',
  REJECTED: 'bg-danger-500/10 text-danger-600',
};
const money = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

function KV({ label, children, tight }: { label: string; children: React.ReactNode; tight?: boolean }) {
  return (
    <div className="flex gap-3 py-1 text-sm">
      <span className={`shrink-0 text-muted ${tight ? '' : 'w-24'}`}>{label}</span>
      <span className="text-content">{children}</span>
    </div>
  );
}

/** Invoice detail — Tailwind. Bill-To + payment progress, seller bank/UPI, and Items / Payment history. */
export function InvoiceDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { message } = useApp();
  const [tab, setTab] = useState<'items' | 'payments'>('items');
  const [noteId, setNoteId] = useState<string | null>(null);

  const { data: inv, isLoading, isError } = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => request<any>({ url: `/invoices/${id}` }),
  });
  const { data: payments } = useQuery({
    queryKey: ['invoice-payments', id],
    queryFn: () => request<any[]>({ url: `/invoices/${id}/payments` }),
    enabled: !!id,
  });

  const openPdf = async () => {
    if (inv?.pdfUrl) { window.open(inv.pdfUrl, '_blank', 'noopener'); return; }
    try {
      const r = await api.get(`/invoices/${id}/pdf`, { responseType: 'blob' });
      window.open(URL.createObjectURL(r.data), '_blank');
    } catch {
      message.error('Failed to load invoice PDF');
    }
  };

  if (isLoading) return <div className="flex justify-center py-16"><Spin tip="Loading…" /></div>;
  if (isError || !inv) {
    return (
      <div className="mx-auto mt-10 max-w-md rounded-card border border-line bg-surface p-8 text-center">
        <div className="text-base font-semibold text-content">Couldn't load this invoice</div>
        <p className="mt-1 text-sm text-muted">It may have been removed, or you may not have access.</p>
        <button onClick={() => navigate('/billing/invoices')} className="mt-4 rounded-control bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700">Back to invoices</button>
      </div>
    );
  }

  const total = Number(inv.grandTotal) || 0;
  const paid = Number(inv.paidAmt) || 0;
  const pct = total > 0 ? Math.min(100, Math.round((paid / total) * 100)) : 0;
  const barColor = pct >= 100 ? 'bg-success-500' : pct > 0 ? 'bg-warning-500' : 'bg-danger-500';
  const items = inv.items ?? [];
  const r = inv.retailer;
  const bd = inv.bankDetails;
  const cgst = Number(inv.cgstAmt) || 0;
  const sgst = Number(inv.sgstAmt) || 0;
  const igst = Number(inv.igstAmt) || 0;
  const roundOff = Number(inv.roundOff) || 0;
  const balPositive = Number(inv.balanceAmt) > 0.01;
  const due = Number(inv.balanceAmt) || 0;
  // UPI deep-link carrying the amount to pay (the outstanding balance), like the proforma / Pay-modal
  // QR. Prefer this generated QR (it embeds the amount) over any uploaded static image, which has none.
  const upiPay = bd?.upiString ? `${bd.upiString}${due > 0.01 ? `&am=${due.toFixed(2)}` : ''}` : '';
  const pays = payments ?? [];
  const totalApplied = pays.reduce((s: number, p: any) => s + Number(p.allocatedAmt || 0), 0);

  const totalsBox = (
    <div className="w-60 shrink-0 overflow-hidden rounded-card border border-line text-sm">
      <TotalRow label="Taxable value" value={money(inv.taxableAmt)} />
      {cgst > 0 && <TotalRow label="CGST" value={money(cgst)} />}
      {sgst > 0 && <TotalRow label="SGST" value={money(sgst)} />}
      {igst > 0 && <TotalRow label="IGST" value={money(igst)} />}
      {cgst === 0 && sgst === 0 && igst === 0 && <TotalRow label="GST" value={money(0)} />}
      {roundOff ? <TotalRow label="Round off" value={money(roundOff)} /> : null}
      <TotalRow label="Grand Total" value={<span className="text-base font-bold text-brand-700">{money(inv.grandTotal)}</span>} />
      <TotalRow label="Paid" value={money(inv.paidAmt)} />
      <TotalRow label="Balance" value={<span className={`font-semibold ${balPositive ? 'text-danger-600' : 'text-success-600'}`}>{money(inv.balanceAmt)}</span>} />
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-content">{inv.invoiceNo}</h1>
          <span className={`inline-flex items-center rounded-pill px-2.5 py-0.5 text-xs font-medium ${INV_PILL[inv.status] ?? 'bg-surface-3 text-subtle'}`}>{inv.status}</span>
        </div>
        <div className="flex items-center gap-2">
          <ProposeCdButton invoiceId={id} />
          <button onClick={openPdf} className="inline-flex items-center gap-2 rounded-control bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700">
            <FileText size={16} /> Download PDF
          </button>
        </div>
      </div>

      {/* Bill To + Invoice meta */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <section className="rounded-card border border-line bg-surface p-4">
          <h2 className="mb-2 flex items-center gap-2 text-base font-semibold text-content"><Store size={16} /> Bill To</h2>
          {r ? (
            <div className="divide-y divide-line/60">
              <KV label="Customer"><b className="font-semibold">{r.companyName}</b>{r.code ? <span className="text-muted"> · {r.code}</span> : ''}</KV>
              {r.contactPerson && <KV label="Contact">{r.contactPerson}</KV>}
              <KV label="Address"><span className="inline-flex items-start gap-1"><MapPin size={16} className="mt-0.5 text-muted" /> {[r.addressLine1, r.addressLine2, r.city, r.stateCode, r.pincode].filter(Boolean).join(', ')}</span></KV>
              <KV label="Mobile">{r.mobile}</KV>
              <KV label="GSTIN">{r.gstNumber ?? '—'}</KV>
            </div>
          ) : <p className="text-sm text-muted">Retailer #{inv.retailerId}</p>}
        </section>

        <section className="rounded-card border border-line bg-surface p-4">
          <h2 className="mb-2 text-base font-semibold text-content">Invoice</h2>
          <div className="grid grid-cols-2 gap-x-4">
            <KV label="Invoice date">{formatIstDate(inv.invoiceDate)}</KV>
            <KV label="Due date">{formatIstDate(inv.dueDate)}</KV>
            {inv.orderId && (
              <div className="col-span-2">
                <KV label="Order"><button onClick={() => navigate(`/orders/${inv.orderId}`)} className="font-medium text-brand-700 hover:underline">{inv.orderNo ?? 'View order'}</button></KV>
              </div>
            )}
          </div>
          <div className="mt-3">
            <div className="h-2 w-full overflow-hidden rounded-pill bg-surface-3">
              <div className={`h-full rounded-pill ${barColor}`} style={{ width: `${Math.max(pct, 2)}%` }} />
            </div>
            <div className="mt-1.5 text-xs text-muted">
              Paid <span className="font-semibold text-content">{money(paid)}</span> of {money(total)} · Balance{' '}
              <span className={`font-semibold ${balPositive ? 'text-danger-600' : 'text-success-600'}`}>{money(inv.balanceAmt)}</span>
            </div>
          </div>
        </section>
      </div>

      {/* Credit notes raised against this invoice (sales-return credit-note fan-out is invoice-scoped). */}
      {(inv.creditNotes?.length ?? 0) > 0 && (
        <section className="rounded-card border border-line bg-surface p-4">
          <h2 className="mb-2 flex items-center gap-2 text-base font-semibold text-content"><FileText size={16} /> Credit notes</h2>
          <div className="divide-y divide-line/60">
            {inv.creditNotes.map((cn: any) => {
              const cancelled = cn.status === 'CANCELLED';
              return (
                <div key={cn.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <button onClick={() => setNoteId(String(cn.id))} className="font-medium text-brand-700 hover:underline dark:text-brand-400">{cn.noteNo}</button>
                  <div className="flex items-center gap-3">
                    <span className="text-muted">{cn.noteDate ? String(cn.noteDate).substring(0, 10) : ''}</span>
                    {cancelled && <span className="rounded-pill bg-danger-500/10 px-1.5 py-0.5 text-2xs font-medium text-danger-600">reversed</span>}
                    <span className={cancelled ? 'text-subtle line-through' : 'font-semibold text-content'}>{money(cn.amount)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Pay to — Bank details */}
      {bd && (
        <section className="rounded-card border border-line bg-surface p-4">
          <h2 className="mb-3 flex items-center gap-2 text-base font-semibold text-content"><Landmark size={16} /> Pay to — Bank details</h2>
          <div className="flex flex-wrap items-start gap-x-10 gap-y-6">
            <div className="grid grid-cols-[auto_auto] gap-x-12 gap-y-1.5">
              <KV label="Account holder" tight><b className="font-semibold">{bd.accountHolderName}</b></KV>
              <KV label="Bank" tight>{bd.bankName}{bd.label ? ` · ${bd.label}` : ''}</KV>
              <KV label="A/c No." tight>{bd.accountNumber}</KV>
              <KV label="IFSC" tight>{bd.ifsc}</KV>
              {bd.branch && <KV label="Branch" tight>{bd.branch}</KV>}
              {bd.upiId && <KV label="UPI ID" tight>{bd.upiId}</KV>}
              {bd.notes && <p className="col-span-2 mt-1 text-xs text-muted">{bd.notes}</p>}
            </div>
            <div className="ml-auto flex items-start gap-5">
              {totalsBox}
              {(upiPay || bd.upiQrUrl) && (
                <div className="shrink-0 text-center">
                  {upiPay
                    ? <QRCode value={upiPay} size={132} bordered={false} />
                    : <img src={bd.upiQrUrl} width={132} height={132} className="object-contain" alt="UPI QR" />}
                  <div className="mt-1 text-2xs text-muted">Scan to pay{due > 0.01 ? <span className="font-semibold text-content"> {money(due)}</span> : ''} via UPI</div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Items / Payment history */}
      <section className="overflow-hidden rounded-card border border-line bg-surface">
        <div className="flex border-b border-line">
          {(['items', 'payments'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-3 text-sm font-medium ${tab === t ? 'border-b-2 border-brand-600 text-brand-700' : 'text-muted hover:text-content'}`}
            >
              {t === 'items' ? `Items (${items.length})` : `Payment history (${pays.length})`}
            </button>
          ))}
        </div>

        <div className="p-4">
          {tab === 'items' ? (
            <>
              {inv.itemsFromOrder && items.length > 0 && (
                <p className="mb-3 text-xs text-muted">Line items are shown from the linked order — this Tally invoice stores only the totals (below). Download the PDF for the exact tax invoice.</p>
              )}
              {items.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[760px] text-sm">
                    <thead>
                      <tr className="border-b border-line text-left text-xs text-muted">
                        <th className="px-3 py-2.5 text-2xs font-semibold uppercase tracking-wide text-muted">Product</th>
                        <th className="px-3 py-2.5 text-2xs font-semibold uppercase tracking-wide text-muted">HSN</th>
                        <th className="px-3 py-2.5 text-right text-2xs font-semibold uppercase tracking-wide text-muted">Qty</th>
                        <th className="px-3 py-2.5 text-right text-2xs font-semibold uppercase tracking-wide text-muted">Rate</th>
                        <th className="px-3 py-2.5 text-right text-2xs font-semibold uppercase tracking-wide text-muted">Disc %</th>
                        <th className="px-3 py-2.5 text-right text-2xs font-semibold uppercase tracking-wide text-muted">Taxable</th>
                        <th className="px-3 py-2.5 text-right text-2xs font-semibold uppercase tracking-wide text-muted">CGST</th>
                        <th className="px-3 py-2.5 text-right text-2xs font-semibold uppercase tracking-wide text-muted">SGST</th>
                        <th className="px-3 py-2.5 text-right text-2xs font-semibold uppercase tracking-wide text-muted">IGST</th>
                        <th className="px-3 py-2.5 text-right text-2xs font-semibold uppercase tracking-wide text-muted">Line Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((it: any, idx: number) => (
                        <tr key={it.id ?? idx} className="border-b border-line/60 align-top">
                          <td className="px-3 py-2.5">
                            <div className="font-medium text-content">{it.productName}</div>
                            {it.batches?.length > 0 && (
                              <div className="mt-1.5">
                                <div className="text-2xs text-muted">Allocated batches</div>
                                <div className="mt-1 flex flex-col gap-1">
                                  {it.batches.map((b: any, i: number) => (
                                    <span key={i} className="flex flex-wrap items-center gap-1.5 text-xs">
                                      <code className="rounded bg-surface-2 px-1.5 py-0.5 text-2xs text-content">{b.batchNo}</code>
                                      <span className="inline-flex items-center rounded-pill bg-sky-500/10 px-1.5 py-0.5 text-2xs font-medium text-sky-600">×{Number(b.qty)}</span>
                                      {b.expiry && <span className="text-2xs text-muted">exp {formatIstDate(b.expiry)}</span>}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-content">{it.hsnCode ?? '—'}</td>
                          <td className="px-3 py-2.5 text-right text-content">
                            {(() => {
                              const phrase = boxLoosePhrase(it.boxQuantity, it.looseUnitQuantity, it.unitsPerBoxSnapshot);
                              return phrase
                                ? <span><b>{phrase}</b><div className="text-2xs text-muted">= {fmtQty(it.qty)} units</div></span>
                                : fmtQty(Number(it.qty));
                            })()}
                          </td>
                          <td className="px-3 py-2.5 text-right text-content">{money(it.unitPrice)}</td>
                          <td className="px-3 py-2.5 text-right text-content">{Number(it.discountPct ?? 0)}%</td>
                          <td className="px-3 py-2.5 text-right text-content">{money(it.taxableAmt)}</td>
                          <td className="px-3 py-2.5 text-right text-content">{money(it.cgstAmt)}</td>
                          <td className="px-3 py-2.5 text-right text-content">{money(it.sgstAmt)}</td>
                          <td className="px-3 py-2.5 text-right text-content">{money(it.igstAmt)}</td>
                          <td className="px-3 py-2.5 text-right font-semibold text-content">{money(it.lineTotal)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="py-10 text-center text-sm text-muted">No line items on this invoice</div>
              )}

              {/* Totals — normally shown beside the QR in the Pay-to panel; here only as a fallback
                  when there's no bank panel to host them. */}
              {!bd && <div className="mt-4 flex justify-end">{totalsBox}</div>}
            </>
          ) : (
            pays.length > 0 ? (
              <div className="flex flex-col gap-3">
                {pays.map((p: any, i: number) => {
                  const files: string[] = p.attachmentUrls ?? [];
                  return (
                    <div key={i} className="rounded-card border border-line p-4">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-content">{p.paymentNo}</span>
                          {p.approvalStatus && <span className={`inline-flex items-center rounded-pill px-2 py-0.5 text-2xs font-medium ${PAY_PILL[p.approvalStatus] ?? 'bg-surface-3 text-subtle'}`}>{p.approvalStatus}</span>}
                        </div>
                        <div className="text-right">
                          <div className="text-base font-bold text-content">{money(p.amount)}</div>
                          <div className="text-2xs text-muted">applied {money(p.allocatedAmt)}</div>
                        </div>
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-3">
                        <PField label="Date">{formatIstDate(p.paymentDate)}</PField>
                        <PField label="Mode">{p.fromCredit ? <span className="font-medium text-brand-700 dark:text-brand-400">Account credit</span> : p.mode}</PField>
                        <PField label="Reference No">{p.referenceNo || '—'}</PField>
                        <PField label="UTR / Bank Ref"><span className="font-medium">{p.utr || '—'}</span></PField>
                        <PField label="Submitted by">{p.submittedByName || '—'}{p.submittedByRole ? <span className="text-muted"> · {p.submittedByRole}</span> : ''}</PField>
                        {p.approvedByName && <PField label="Approved by">{p.approvedByName}{p.approvedAt ? <span className="text-muted"> · {formatIstDate(p.approvedAt)}</span> : ''}</PField>}
                      </div>
                      {p.rejectedReason && <div className="mt-2 rounded-card border border-danger-500/20 bg-danger-500/10 px-3 py-2 text-xs text-danger-600"><b>Rejected:</b> {p.rejectedReason}</div>}
                      <div className="mt-3">
                        <div className="mb-1.5 text-xs font-medium text-muted">Payment proof</div>
                        {files.length ? (
                          <Image.PreviewGroup>
                            <div className="flex flex-wrap gap-2">
                              {files.map((u, j) => (/\.pdf($|\?)/i.test(u)
                                ? <a key={j} href={u} target="_blank" rel="noopener" className="rounded-card border border-line px-3 py-2 text-xs text-brand-700 hover:bg-surface-2">Proof {j + 1} (PDF)</a>
                                : <Image key={j} src={u} width={104} height={104} className="rounded-card object-cover" />))}
                            </div>
                          </Image.PreviewGroup>
                        ) : <span className="text-xs text-muted">No proof attached</span>}
                      </div>
                    </div>
                  );
                })}
                <div className="flex justify-end border-t border-line pt-3 text-sm">
                  <span className="text-muted">Total applied to this invoice:&nbsp;</span>
                  <span className="ml-1 font-bold text-content">{money(totalApplied)}</span>
                </div>
              </div>
            ) : (
              <div className="py-10 text-center text-sm text-muted">No payments recorded against this invoice's order yet</div>
            )
          )}
        </div>
      </section>

      <CreditNoteDrawer id={noteId} onClose={() => setNoteId(null)} />
    </div>
  );
}

/** Accountant entry point: check the invoice's prompt-payment CD eligibility and create a PENDING adjustment. */
function ProposeCdButton({ invoiceId }: { invoiceId: string }) {
  const canCreate = usePermission('DISCOUNT.ADJUST.CREATE');
  const [open, setOpen] = useState(false);
  const { message } = useApp();
  const { data: elig, isFetching } = useQuery({
    queryKey: ['cd-eligibility', invoiceId],
    queryFn: () => request<any>({ url: '/discount-adjustments/eligibility', params: { invoiceId } }),
    enabled: open,
  });
  const propose = useMutation({
    mutationFn: () => request<any>({ url: '/discount-adjustments/propose', method: 'POST', data: { invoiceId } }),
    onSuccess: (r: any) => { message.success(`Proposed ${r.adjNo} — pending approval`); setOpen(false); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });
  if (!canCreate) return null;
  const m = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  return (
    <>
      <button onClick={() => setOpen(true)} className="inline-flex items-center gap-2 rounded-control border border-line px-4 py-2 text-sm font-medium text-content hover:bg-surface-2">
        <Percent size={16} /> Prompt-payment discount
      </button>
      <Modal open={open} onOpenChange={(o) => { if (!o) setOpen(false); }} title="Propose prompt-payment cash discount">
        {isFetching ? <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div> : elig ? (
          elig.eligible ? (
            <>
              <Descriptions size="small" column={1} bordered>
                <Descriptions.Item label="Discount">{elig.pct}% of {m(elig.basisAmount)} = <b>{m(elig.amount)}</b></Descriptions.Item>
                <Descriptions.Item label="Basis">Approved payment on {elig.paymentDate} — {elig.daysFromInvoice} days after invoice</Descriptions.Item>
              </Descriptions>
              <Alert type="info" showIcon style={{ marginTop: 12 }} message="Creates a PENDING adjustment. An approver posts the ledger credit with remarks — the tax invoice is not changed." />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                <button onClick={() => setOpen(false)} className="rounded-control border border-line px-4 py-2 text-sm">Cancel</button>
                <button onClick={() => propose.mutate()} disabled={propose.isPending} className="rounded-control bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-60">Propose</button>
              </div>
            </>
          ) : <Alert type="warning" showIcon message={elig.reason ?? 'Not eligible for a prompt-payment discount.'} />
        ) : null}
      </Modal>
    </>
  );
}

function PField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-2xs text-muted">{label}</div>
      <div className="text-sm text-content">{children}</div>
    </div>
  );
}

function TotalRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between border-b border-line/60 px-3 py-1.5 last:border-b-0">
      <span className="text-muted">{label}</span>
      <span className="text-content">{value}</span>
    </div>
  );
}
